import { Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, IndianRupee, Bookmark, BookmarkCheck, ArrowRight, BadgeCheck, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, formatSalary, WORK_MODES, JOB_TYPES, expLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function useSaveToggle(savedIds, setSavedIds) {
  const { user } = useAuth();
  const nav = useNavigate();
  return async (job) => {
    if (!user) return nav(`/login?next=/jobs/${job.slug}`);
    if (user.role !== "seeker") return toast.info("Only job seeker accounts can save jobs");
    const saved = savedIds.includes(job.id);
    try {
      await (saved ? api.delete(`/jobs/${job.id}/save`) : api.post(`/jobs/${job.id}/save`));
      setSavedIds(saved ? savedIds.filter((x) => x !== job.id) : [...savedIds, job.id]);
      toast.success(saved ? "Removed from saved jobs" : "Job saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
}

export function CompanyLogo({ job, size = 40 }) {
  const src = fileUrl(job.company_logo_id || job.logo_id);
  return (
    <span className="grid shrink-0 place-items-center overflow-hidden rounded-lg bg-[#EAF8EF] text-[#009a41]" style={{ width: size, height: size }}>
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <Building2 size={size / 2} />}
    </span>
  );
}

export function JobCard({ job, saved, onSave }) {
  return (
    <article className="card-hover flex flex-col p-5" data-testid={`job-card-${job.id}`}>
      <div className="flex items-start gap-3">
        <CompanyLogo job={job} />
        <div className="min-w-0">
          <Link to={`/jobs/${job.slug}`} className="line-clamp-2 font-semibold leading-snug text-[#0B1B2B] hover:text-[#00B94F]" data-testid="job-card-title">{job.title}</Link>
          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500">{job.company_name}{job.company_verified && <BadgeCheck size={13} className="text-[#00B94F]" />}</p>
        </div>
      </div>
      <ul className="mt-4 space-y-1.5 text-[13px] text-slate-600">
        <li className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" />{job.work_mode === "remote" ? "Remote" : job.location}</li>
        <li className="flex items-center gap-2"><Clock size={14} className="text-slate-400" />{JOB_TYPES[job.job_type]}</li>
        <li className="flex items-center gap-2"><IndianRupee size={14} className="text-slate-400" />{formatSalary(job)}</li>
      </ul>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className="chip">{job.category_name}</span>
        <span className="chip">{expLabel(job)}</span>
        <span className="chip">{WORK_MODES[job.work_mode]}</span>
      </div>
      <div className="mt-5 flex items-center gap-2 pt-1">
        <button onClick={() => onSave?.(job)} className="btn-outline btn-sm" data-testid={`save-job-${job.id}`} aria-label="Save job">
          {saved ? <BookmarkCheck size={14} className="text-[#00B94F]" /> : <Bookmark size={14} />}{saved ? "Saved" : "Save"}
        </button>
        <Link to={`/jobs/${job.slug}?apply=1`} className="btn-green btn-sm flex-1" data-testid={`apply-job-${job.id}`}>Apply now <ArrowRight size={14} /></Link>
      </div>
    </article>
  );
}

export function Empty({ title, text, action }) {
  return (
    <div className="card flex flex-col items-center px-6 py-14 text-center" data-testid="empty-state">
      <p className="font-semibold">{title}</p>
      {text && <p className="mt-1 max-w-sm text-sm text-slate-500">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
